"use client";

import { IS_PRODUCTION, WHITE_LABEL_URL } from "@/constants/envs";
import {
  CAMPAIGN_ID_QUERY_KEY,
  CAMPAIGN_ID_STORE_NAME,
} from "@/constants/company";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

/**
 * This hook is used to handle the functionalities around campaigns
 * Setting the value of the campaign id in the storage and transferring it to the other website
 */
const useCampaign = () => {
  const searchParams = useSearchParams();

  const baseRegisterUrl = IS_PRODUCTION
    ? `${WHITE_LABEL_URL}/register`
    : WHITE_LABEL_URL || "";
  const baseLoginUrl = IS_PRODUCTION
    ? `${WHITE_LABEL_URL}/login`
    : WHITE_LABEL_URL || "";

  const [registerLink, setRegisterLink] = useState<string>(baseRegisterUrl);
  const [loginLink, setLoginLink] = useState<string>(baseLoginUrl);
  const campaignId = searchParams.get(CAMPAIGN_ID_QUERY_KEY);

  /** add this function to the sign up and login links on click */
  const handleClearCampaignId = () => {
    localStorage.removeItem(CAMPAIGN_ID_STORE_NAME);
  };

  /**
   * This checks the local storage on load for the campaign value.
   * If it exists, it then appends it to the login and signup urls to be navigated to
   */
  useEffect(() => {
    const link = localStorage.getItem(CAMPAIGN_ID_STORE_NAME);
    if (!link) return;

    const loginLinkUpdate = `${WHITE_LABEL_URL}/login?${CAMPAIGN_ID_QUERY_KEY}=${link}`;
    const registerLinkUpdate = `${WHITE_LABEL_URL}/register?${CAMPAIGN_ID_QUERY_KEY}=${link}`;

    setLoginLink(loginLinkUpdate);
    setRegisterLink(registerLinkUpdate);
  }, []);

  /**
   * This checks for the query with key of 'c' in the url and stores it in the storage
   */
  useEffect(() => {
    if (campaignId) {
      localStorage.setItem(CAMPAIGN_ID_STORE_NAME, campaignId);
    }
  }, [campaignId]);

  return { registerLink, loginLink, handleClearCampaignId };
};

export default useCampaign;
